import React, { useState, useEffect, useCallback } from 'react'
import Button from 'components/shared/Button'
import Drawer from 'components/shared/Drawer'
import TableComponent from 'components/shared/TableComponent'
import SimplePagination from 'components/shared/SimplePagination.js/index'
import { getPaginationHeaders } from 'constants/utilityFunctions'
import Loader from 'components/shared/Loader'
import { Alert } from '@material-ui/lab'
import { Toast } from 'components/shared/Toaster'
import AuthService from 'tools/AuthService'
import MotusSearchDrawer from './MotusSearchDrawer'
import MotusDotDetail from './MotusDotDetail'
import { getDotColumns, getNameColumns } from './motusColumns'
import {
    getMotusDotSearches,
    getMotusNameSearches,
    getMotusDotSearchDetail,
    getMotusNameSearchDetail,
    performMotusDotSearch,
    performMotusNameSearch,
    getSearchedDotNumbers
} from './motusApi'
import MotusNameDetail from "./MotusNameDetail";
import ConfirmationDialog from "../../../../ConfirmationDialog";

const MotusTab = ({ creditCosts, applicationId, businessName, dot }) => {
    const { isReadOnlyUser } = AuthService.getClientFeatures()

    // Search drawer state
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [searchType, setSearchType] = useState('dot')
    const [searchLoading, setSearchLoading] = useState(false)

    // Search inputs
    const [dotInput, setDotInput] = useState(dot || '')
    const [nameInput, setNameInput] = useState(businessName || '')

    // DOT searches table state
    const [dotSearches, setDotSearches] = useState(null)
    const [dotSearchesLoading, setDotSearchesLoading] = useState(false)
    const [dotPaginationParams, setDotPaginationParams] = useState({})

    // Name searches table state
    const [nameSearches, setNameSearches] = useState(null)
    const [nameSearchesLoading, setNameSearchesLoading] = useState(false)
    const [namePaginationParams, setNamePaginationParams] = useState({})

    // Detail drawer state
    const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailType, setDetailType] = useState(null)

    // Confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirmAction, setConfirmAction] = useState(null)

    // Searched DOT numbers
    const [searchedDots, setSearchedDots] = useState([])

    const fetchSearchedDots = useCallback(() => {
        if (!applicationId) return
        getSearchedDotNumbers(applicationId)
            .then(res => setSearchedDots(res || []))
            .catch(err => console.error('Error fetching searched DOT numbers:', err))
    }, [applicationId])

    useEffect(() => { fetchSearchedDots() }, [fetchSearchedDots])

    // Sync pre-populated fields
    useEffect(() => { if (dot) setDotInput(dot) }, [dot])
    useEffect(() => { if (businessName) setNameInput(businessName) }, [businessName])

    // Fetch DOT searches
    const fetchDotSearches = useCallback(() => {
        if (!applicationId) return
        setDotSearchesLoading(true)
        getMotusDotSearches(applicationId, dotPaginationParams)
            .then(res => setDotSearches(res))
            .catch(err => console.error('Error fetching Motus DOT searches:', err))
            .finally(() => setDotSearchesLoading(false))
    }, [applicationId, dotPaginationParams])

    // Fetch Name searches
    const fetchNameSearches = useCallback(() => {
        if (!applicationId) return
        setNameSearchesLoading(true)
        getMotusNameSearches(applicationId, namePaginationParams)
            .then(res => setNameSearches(res))
            .catch(err => console.error('Error fetching Motus name searches:', err))
            .finally(() => setNameSearchesLoading(false))
    }, [applicationId, namePaginationParams])

    useEffect(() => { fetchDotSearches() }, [fetchDotSearches])
    useEffect(() => { fetchNameSearches() }, [fetchNameSearches])

    // Helper to check if DOT was already searched
    const isDotAlreadySearched = (dotNumber) => {
        return searchedDots.includes(dotNumber)
    }

    const { creditUsageEnabled } = AuthService.getClientFeatures()

    const getMotusDotCreditCost = () => {
        if (!creditCosts) return 0
        const costObj = creditCosts.find(cost => cost.requestType === 'MOTUS_DOT')
        return costObj ? costObj.creditCost : 0
    }

    const getMotusNameCreditCost = () => {
        if (!creditCosts) return 0
        const costObj = creditCosts.find(cost => cost.requestType === 'MOTUS_NAME')
        return costObj ? costObj.creditCost : 0
    }

    // Perform search

    const handleSearch = async () => {
        if (searchType === 'dot' && dotInput && isDotAlreadySearched(dotInput)) {
            setConfirmAction(() => () => executeSearch())
            setConfirmOpen(true)
            return
        }
        executeSearch()
    }


    const executeSearch = async () => {
        setSearchLoading(true)
        try {
            if (searchType === 'dot') {
                if (!dotInput) {
                    Toast({ type: 'error', message: 'Please enter a DOT number.' })
                    return
                }
                const res = await performMotusDotSearch(applicationId, { dotNumber: dotInput })
                Toast({ type: 'success', message: 'Motus DOT search completed.' })
                fetchDotSearches()
                fetchSearchedDots()
                setDrawerOpen(false)
                setDetailType('dot')
                setDetailData(res)
                setDetailDrawerOpen(true)
            } else {
                if (!nameInput) {
                    Toast({ type: 'error', message: 'Please enter a name.' })
                    return
                }
                const res = await performMotusNameSearch(applicationId, { name: nameInput })
                Toast({ type: 'success', message: 'Motus name search completed.' })
                fetchNameSearches()
                fetchSearchedDots()
                setDrawerOpen(false)
                setDetailType('name')
                setDetailData(res)
                setDetailDrawerOpen(true)
            }
        } catch (err) {
            console.error('Motus search error:', err)
            // The failed search is still recorded (status ERROR), so refresh the history list
            if (searchType === 'dot') fetchDotSearches()
            else fetchNameSearches()
        } finally {
            setSearchLoading(false)
        }
    }

    const handleDotSearchFromNameResult = async (dotNumber) => {
        if (isDotAlreadySearched(dotNumber)) {
            setConfirmAction(() => () => executeDotSearchFromName(dotNumber))
            setConfirmOpen(true)
            return
        }
        executeDotSearchFromName(dotNumber)
    }

    const executeDotSearchFromName = async (dotNumber) => {
        setDetailLoading(true)
        try {
            const res = await performMotusDotSearch(applicationId, { dotNumber })
            Toast({ type: 'success', message: 'Motus DOT search completed.' })
            fetchDotSearches()
            fetchSearchedDots()
            setDetailType('dot')
            setDetailData(res)
        } catch (err) {
            console.error('Error performing DOT search from name result:', err)
        } finally {
            setDetailLoading(false)
        }
    }

    // View detail handlers
    const handleViewDotDetail = async (searchUuid) => {
        setDetailLoading(true)
        setDetailType('dot')
        setDetailDrawerOpen(true)
        try {
            const res = await getMotusDotSearchDetail(searchUuid)
            setDetailData(res)
        } catch (err) {
            console.error('Error fetching DOT search detail:', err)
        } finally {
            setDetailLoading(false)
        }
    }


    const handleViewNameDetail = async (searchUuid) => {
        setDetailLoading(true)
        setDetailType('name')
        setDetailDrawerOpen(true)
        fetchSearchedDots()
        try {
            const res = await getMotusNameSearchDetail(searchUuid)
            setDetailData(res)
        } catch (err) {
            console.error('Error fetching name search detail:', err)
        } finally {
            setDetailLoading(false)
        }
    }

    const dotSearchesData = dotSearches?.response || []
    const dotPaginationInfo = getPaginationHeaders(dotSearches)
    const nameSearchesData = nameSearches?.response || []
    const namePaginationInfo = getPaginationHeaders(nameSearches)

    return (
        <div className='m-3'>
            {/* Header */}
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='mb-0'>Motus Searches</h5>
                {!isReadOnlyUser && (
                    <Button variant='contained' color='primary' onClick={() => setDrawerOpen(true)}>
                        New Motus Search
                    </Button>
                )}
            </div>

            {/* DOT Searches Table */}
            <div className='cardStyle mb-3'>
                <h6 className='mb-2'>DOT Searches</h6>
                {dotSearchesLoading ? (
                    <Loader />
                ) : dotSearchesData.length > 0 ? (
                    <>
                        <TableComponent
                            keyField='uuid'
                            columns={getDotColumns(handleViewDotDetail)}
                            data={dotSearchesData}
                            showPagination={false}
                        />
                        {dotPaginationInfo?.totalRecords > 0 && (
                            <SimplePagination
                                onChangePagination={setDotPaginationParams}
                                totalRecords={dotPaginationInfo.totalRecords}
                                totalPages={dotPaginationInfo.totalPages}
                            />
                        )}
                    </>
                ) : (
                    <Alert severity="info" className='mt-2'>No DOT searches found.</Alert>
                )}
            </div>

            {/* Name Searches Table */}
            <div className='cardStyle mb-3'>
                <h6 className='mb-2'>Name Searches</h6>
                {nameSearchesLoading ? (
                    <Loader />
                ) : nameSearchesData.length > 0 ? (
                    <>
                        <TableComponent
                            keyField='uuid'
                            columns={getNameColumns(handleViewNameDetail)}
                            data={nameSearchesData}
                            showPagination={false}
                        />
                        {namePaginationInfo?.totalRecords > 0 && (
                            <SimplePagination
                                onChangePagination={setNamePaginationParams}
                                totalRecords={namePaginationInfo.totalRecords}
                                totalPages={namePaginationInfo.totalPages}
                            />
                        )}
                    </>
                ) : (
                    <Alert severity="info" className='mt-2'>No name searches found.</Alert>
                )}
            </div>

            {/* Search Drawer */}

            <MotusSearchDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                searchType={searchType}
                setSearchType={setSearchType}
                dotInput={dotInput}
                setDotInput={setDotInput}
                nameInput={nameInput}
                setNameInput={setNameInput}
                onSearch={handleSearch}
                loading={searchLoading}
                dotAlreadySearched={searchType === 'dot' && isDotAlreadySearched(dotInput)}
                creditCost={searchType === 'dot' ? getMotusDotCreditCost() : getMotusNameCreditCost()}
                creditUsageEnabled={creditUsageEnabled}
            />
            {/* Detail Drawer */}
            <Drawer
                open={detailDrawerOpen}
                onClose={() => { setDetailDrawerOpen(false); setDetailData(null) }}
                title={detailType === 'dot' ? 'DOT Search Detail' : 'Name Search Detail'}
                width='medium'
            >
                <div className='p-3'>
                    {detailLoading ? (
                        <Loader />
                    ) : detailData && detailType === 'dot' ? (
                        <MotusDotDetail data={detailData} />
                    ) : detailData && detailType === 'name' ? (

                        <MotusNameDetail
                            data={detailData}
                            onDotSearch={handleDotSearchFromNameResult}
                            dotSearchLoading={detailLoading}
                            searchedDots={searchedDots}
                        />
                    ) : (
                        <Alert severity="info">No data available.</Alert>
                    )}
                </div>
            </Drawer>

            {/* Duplicate DOT search confirmation */}
            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => {setConfirmOpen(false)
                    if (confirmAction) confirmAction()
                }}
                title="DOT Already Searched"
                message="This DOT number has already been searched. Do you want to search again?"
                confirmText="Search Again"
                cancelText="Cancel"
            />
        </div>
    )
}

export default MotusTab